import * as t from 'io-ts';
import _ from 'lodash';

import {makeApiType, RCardIdRef, RContactUidRef} from './apiTypes';
import {parseAutoBcc} from './channelCommonTypes';
import {makeSafeArrayType} from './types/arrayTypes';
import {makeEnumRuntimeType, makeEnumTypeGuard} from './types/enumTypes';
import {makeOptional} from './types/optionalType';

/*
 * Runtime type.
 */

export enum ContactSourcesEnum {
  LAYER = 'layer',
  CUSTOM = 'custom',
  FACEBOOK = 'facebook',
  EMAIL = 'email',
  INTERCOM = 'intercom',
  SMOOCH = 'smooch',
  PHONE = 'phone',
  TWITTER = 'twitter',
  TWITTER_DM = 'twitterdm',
  GOOGLE_PLAY = 'googleplay',
  FRONT_CHAT = 'front_chat',
}
export const RContactSource = makeEnumRuntimeType(ContactSourcesEnum);

const RequiredProperties = t.type({
  uid: t.string,
  source: RContactSource,
  handle: t.string,
  cardIds: t.dictionary(t.string, makeOptional(RCardIdRef)),
});

const OptionalProperties = t.partial({
  url: t.string,
  id: t.Integer,
  channelId: t.Integer,
  avatarUrl: t.string,
  fallbackContactUid: RContactUidRef,
  display_name: t.string,
});

export const RContact = t.readonly(t.intersection([RequiredProperties, OptionalProperties]), 'contact');

/*
 * Type.
 */

export interface ContactModel extends t.TypeOf<typeof RContact> {}
export const RContacts = makeSafeArrayType(RContact);
RContactUidRef.setTargetType(RContact);

/*
 * Type guard.
 */

export const isContactSourcesEnum = makeEnumTypeGuard(ContactSourcesEnum);

/*
 * IO methods.
 */

export function buildContactUidFromContactBlob(src: any): string {
  const source = parseContactSource(src.source);
  // FIXME: some (old) contacts have lost their handle in the DB. We do this to avoid crashing.
  const handle = src.handle || 'noemail@frontapp.com';

  return buildContactUid(source, handle);
}

export function parseContact(src: any): ContactModel {
  return {
    uid: buildContactUidFromContactBlob(src),
    id: src.id,
    url: src.url,
    source: parseContactSource(src.source),
    handle: src.handle,
    cardIds: {},
    channelId: src.channel_id || undefined,
    avatarUrl: src.avatar_url || undefined,
    display_name: src.display_name || undefined,
    fallbackContactUid: parseContactFallbackUid(src.fallback),
  };
}

export function parseContacts(src: any): Array<ContactModel> {
  return src.contacts.map(parseContact);
}

function parseContactFallbackUid(src: any) {
  if (!src) {
    return undefined;
  }
  if (!src.source || !src.handle) {
    return undefined;
  }

  return buildContactUid(src.source, src.handle);
}

export function parseContactSource(source: any): ContactSourcesEnum {
  // Historically, old (<=2017) contacts from Google Play have 'none' as their source.
  // We have yet to fix this in the DB so we're infering it here to avoid errors
  if (source === 'none') {
    return ContactSourcesEnum.GOOGLE_PLAY;
  }

  if (source === 'layer') {
    return ContactSourcesEnum.FRONT_CHAT;
  }

  return source;
}

export function parseContactFromRecipient(src: any): ContactModel {
  // Extract the ID of the attached card and set it as a first card.
  const cardIds = (src.card && {[src.card.namespace]: src.card.id}) || {};
  return {
    ...parseContact(src),
    cardIds,
  };
}

export function parseContactsFromRecipient(src: any): ReadonlyArray<ContactModel> {
  if (!src.fallback) {
    return [parseContactFromRecipient(src)];
  }

  return [parseContactFromRecipient(src), parseContactFromRecipient(src.fallback)];
}

export function parseContactsFromConversation(src: any): ReadonlyArray<ContactModel> {
  return _(src.trackers)
    .map((tracker) => tracker && tracker.recipient)
    .concat(src.contact)
    .compact()
    .map((recipient) => parseContactsFromRecipient(recipient))
    .flatten()
    .uniqBy((contact) => contact.uid)
    .value();
}

export function parseContactFromParticipant(src: any) {
  return buildContactForHandle(ContactSourcesEnum.EMAIL, src.address);
}

export function parseContactsFromChannel(src: any): ReadonlyArray<ContactModel> {
  // Parse the send_as contact.
  const contactSource = parseContactSource(src.contact_source);
  const normalizedSendAs = normalizeHandle(contactSource, src.send_as);

  // If the normalized send-as is empty, return nothing.
  if (!normalizedSendAs) {
    return [];
  }

  const channelAutoBcc = parseAutoBcc(src.settings.autobcc);
  const contactsFromAutoBcc = (channelAutoBcc.handles || []).map((handle) => ({
    uid: `bcc#${buildContactUid(src.contact_source, handle)}`,
    source: parseContactSource(src.contact_source),
    handle,
    cardIds: {},
    channelId: src.id,
    avatarUrl: undefined,
    fallbackContactUid: undefined,
  }));

  const sendAsContact = {
    uid: buildContactUid(src.contact_source, normalizedSendAs),
    source: parseContactSource(src.contact_source),
    handle: normalizedSendAs,
    cardIds: {},
    channelId: src.id,
    avatarUrl: undefined,
    fallbackContactUid: undefined,
  };

  // If address and send_as are the same, nothing else to do.
  const normalizedAddress = normalizeHandle(contactSource, src.address);
  if (normalizedSendAs === normalizedAddress) {
    return [sendAsContact, ...contactsFromAutoBcc];
  }

  // Otherwise, create an address contact.
  const addressContact = {
    uid: buildContactUid(src.contact_source, normalizedAddress),
    source: src.contact_source,
    handle: normalizedAddress,
    cardIds: {},
    channelId: src.id,
    avatarUrl: undefined,
    fallbackContactUid: undefined,
  };

  return [sendAsContact, addressContact, ...contactsFromAutoBcc];
}

export function parseContactInCard(card: any, src: any): ContactModel {
  return {
    ...parseContact(src),
    cardIds: {
      [card.namespace]: card.id,
    },
  };
}

/*
 * Helpers.
 */

export function buildContactForHandle(source: ContactSourcesEnum, handle: string): ContactModel {
  const normalizedHandle = normalizeHandle(source, handle);
  const uid = buildContactUid(source, normalizedHandle);
  return {
    uid,
    source,
    handle: normalizedHandle,
    cardIds: {},
    channelId: undefined,
    avatarUrl: undefined,
    fallbackContactUid: undefined,
  };
}

const normalizePhoneRegexp = /[^\d+]+/g;
export function normalizeHandle(source: ContactSourcesEnum, handle: string) {
  switch (source) {
    case ContactSourcesEnum.PHONE:
      return handle.replace(normalizePhoneRegexp, '');

    default:
      return handle.toLowerCase();
  }
}

export function buildContactFromContactUid(contactUid: string): ContactModel {
  const [source, handle] = parseContactUid(contactUid);
  return {
    uid: contactUid,
    source,
    handle,
    cardIds: {},
  };
}

/*
 * Uid logic.
 */

export const uidDelimiter = '#';
export function buildContactUid(source: ContactSourcesEnum, handle: string): string {
  if (!source || !handle) {
    throw new Error('A source and handle are required.');
  }

  return `${source}${uidDelimiter}${handle}`;
}

export function parseContactUid(uid: string): [ContactSourcesEnum, string] {
  const delimiterIndex = uid.indexOf(uidDelimiter);
  if (delimiterIndex < 0) {
    throw new Error('Invalid UID.');
  }

  // Parse and validate contact source.
  const contactSource = uid.slice(0, delimiterIndex);
  if (!isContactSourcesEnum(contactSource)) {
    throw new Error('Invalid contact source');
  }

  return [contactSource, uid.slice(delimiterIndex + 1)];
}

export function buildContactUidWithoutHandle(source: ContactSourcesEnum) {
  return `${source}${uidDelimiter}`;
}

/*
 * API type.
 */

export const ApiContact = makeApiType('contact', RContact, parseContact);
export const ApiContacts = makeApiType('contacts', RContacts, parseContacts);
